import { useState, type ReactNode } from 'react';
import { errorMessage } from '../../api/client';
import type { WaScope } from '../../api/types';
import {
  useWaCancelCampaign,
  useWaCreateCampaign,
  useWaPauseCampaign,
  useWaPreviewCampaign,
  useWaResumeCampaign,
  useWaSendCampaign,
} from '../../api/whatsapp';
import type {
  CampaignPreview,
  ContactStatus,
  WhatsappCampaign,
  WhatsappTemplate,
} from '../../api/types';
import { Alert, Badge, Button, EmptyState, SelectField, TextField, formatDate } from '../ui';
import { DataTable } from '../table/DataTable';
import { CAMPAIGN_STATUS_COPY, WhatsAppPreview, reasonLabel } from './shared';

/**
 * Compose a campaign from an approved template: pick it, fill its variables
 * (`{{name}}` becomes each person's name), point at a media header, narrow
 * the audience, schedule or send. Preview shows who passes the consent guard
 * and why the rest would not, before anything leaves.
 */
export function CampaignComposer({
  templates,
  campaigns,
  scope = {},
  canManage,
  onChanged,
}: {
  templates: WhatsappTemplate[] | null;
  campaigns: WhatsappCampaign[] | null;
  scope?: WaScope;
  canManage: boolean;
  onChanged: (notice: ReactNode, kind?: 'success' | 'info' | 'error') => void;
}) {
  const approved = (templates ?? []).filter((t) => t.status === 'APPROVED');
  return (
    <div className="stack">
      {canManage ? (
        approved.length ? (
          <ComposeForm templates={approved} scope={scope} onCreated={onChanged} />
        ) : (
          <Alert kind="info">
            Campaigns need an approved template. Build one under Templates and wait for Meta's
            approval.
          </Alert>
        )
      ) : null}
      <h2>Campaigns</h2>
      {campaigns && campaigns.length === 0 ? <EmptyState title="No campaigns yet" /> : null}
      {campaigns && campaigns.length > 0 ? (
        <DataTable
          label="Campaigns"
          columns={[
            { key: 'campaign', header: 'Campaign' },
            { key: 'template', header: 'Template' },
            { key: 'status', header: 'Status' },
            { key: 'recipients', header: 'Recipients', align: 'num' },
            { key: 'sent', header: 'Sent', align: 'num' },
            { key: 'delivered', header: 'Delivered', align: 'num' },
            { key: 'read', header: 'Read', align: 'num' },
            { key: 'replied', header: 'Replied', align: 'num' },
            { key: 'failed', header: 'Failed', align: 'num' },
            { key: 'actions', header: 'Actions', srOnly: true },
          ]}
        >
          {campaigns.map((c) => (
            <CampaignRow
              key={c.id}
              campaign={c}
              scope={scope}
              canManage={canManage}
              onChanged={onChanged}
            />
          ))}
        </DataTable>
      ) : null}
    </div>
  );
}

function CampaignRow({
  campaign,
  scope,
  canManage,
  onChanged,
}: {
  campaign: WhatsappCampaign;
  scope: WaScope;
  canManage: boolean;
  onChanged: (notice: ReactNode, kind?: 'success' | 'info' | 'error') => void;
}) {
  const copy = CAMPAIGN_STATUS_COPY[campaign.status];
  const send = useWaSendCampaign(scope);
  const pause = useWaPauseCampaign(scope);
  const resume = useWaResumeCampaign(scope);
  const cancel = useWaCancelCampaign(scope);
  const preview = useWaPreviewCampaign(scope);
  const acted = (label: string) => (updated: WhatsappCampaign) =>
    onChanged(
      <>
        {label} <strong>{updated.name}</strong>; now{' '}
        {CAMPAIGN_STATUS_COPY[updated.status].label.toLowerCase()}.
      </>,
    );
  const error = send.error ?? pause.error ?? resume.error ?? cancel.error ?? preview.error;
  const { counts } = campaign;

  return (
    <>
      <tr>
        <td>
          {campaign.name}
          {campaign.scheduledAt ? (
            <div className="muted">scheduled {formatDate(campaign.scheduledAt)}</div>
          ) : null}
        </td>
        <td>
          <code>{campaign.templateName}</code>
        </td>
        <td>
          <Badge tone={copy.tone}>{copy.label}</Badge>
        </td>
        <td className="num">{campaign.recipientsTotal}</td>
        <td className="num">{counts.sent}</td>
        <td className="num">{counts.delivered}</td>
        <td className="num">{counts.read}</td>
        <td className="num">{counts.replied}</td>
        <td className="num">{counts.failed}</td>
        <td className="table__actions">
          {campaign.status === 'DRAFT' ||
          campaign.status === 'PAUSED' ||
          campaign.status === 'SCHEDULED' ? (
            <Button
              variant="secondary"
              className="btn--small"
              loading={preview.isPending}
              onClick={() => preview.mutate(campaign.id)}
            >
              Preview
            </Button>
          ) : null}
          {canManage && campaign.status === 'DRAFT' ? (
            <Button
              className="btn--small"
              loading={send.isPending}
              onClick={() => send.mutate({ id: campaign.id }, { onSuccess: acted('Started') })}
            >
              Send
            </Button>
          ) : null}
          {canManage && (campaign.status === 'SENDING' || campaign.status === 'SCHEDULED') ? (
            <Button
              variant="secondary"
              className="btn--small"
              loading={pause.isPending}
              onClick={() => pause.mutate(campaign.id, { onSuccess: acted('Paused') })}
            >
              Pause
            </Button>
          ) : null}
          {canManage && campaign.status === 'PAUSED' ? (
            <Button
              variant="secondary"
              className="btn--small"
              loading={resume.isPending}
              onClick={() => resume.mutate(campaign.id, { onSuccess: acted('Resumed') })}
            >
              Resume
            </Button>
          ) : null}
          {canManage && !['COMPLETED', 'CANCELED'].includes(campaign.status) ? (
            <Button
              variant="danger"
              className="btn--small"
              loading={cancel.isPending}
              onClick={() => {
                if (window.confirm(`Cancel "${campaign.name}"? Queued messages will not be sent.`))
                  cancel.mutate(campaign.id, { onSuccess: acted('Canceled') });
              }}
            >
              Cancel
            </Button>
          ) : null}
        </td>
      </tr>
      {error || campaign.lastError || counts.skipped ? (
        <tr className="table__detail">
          <td colSpan={11}>
            {error ? <Alert kind="error">{errorMessage(error)}</Alert> : null}
            {campaign.lastError ? <span className="muted">{campaign.lastError}. </span> : null}
            {counts.skipped ? (
              <span className="muted">{counts.skipped} skipped by the consent check.</span>
            ) : null}
          </td>
        </tr>
      ) : null}
      {preview.data ? (
        <tr className="table__detail">
          <td colSpan={11}>
            <PreviewPanel preview={preview.data} onClose={() => preview.reset()} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function PreviewPanel({ preview, onClose }: { preview: CampaignPreview; onClose: () => void }) {
  const reasons = Object.entries(preview.skipped).sort((a, b) => b[1] - a[1]);
  return (
    <div className="stack">
      <div className="form__row form__row--between">
        <span>
          <strong>{preview.eligible}</strong> of {preview.total} matched contacts would receive this
          campaign right now.
        </span>
        <Button variant="ghost" className="btn--small" onClick={onClose}>
          Close
        </Button>
      </div>
      {reasons.length ? (
        <ul className="plan__features">
          {reasons.map(([reason, n]) => (
            <li key={reason}>
              {n} × {reasonLabel(reason)}
            </li>
          ))}
        </ul>
      ) : null}
      {preview.sample.length ? (
        <DataTable
          label="Recipient preview"
          nested
          columns={[
            { key: 'contact', header: 'Contact' },
            { key: 'name', header: 'Name' },
            { key: 'verdict', header: 'Verdict' },
          ]}
        >
          {preview.sample.map((s) => (
            <tr key={s.contactId}>
              <td>
                <code>{s.phoneMasked}</code>
              </td>
              <td>{s.name ?? '—'}</td>
              <td>
                {s.reasons.length ? (
                  <span className="muted">{s.reasons.map(reasonLabel).join(', ')}</span>
                ) : (
                  <Badge tone="ok">Will send</Badge>
                )}
              </td>
            </tr>
          ))}
        </DataTable>
      ) : null}
    </div>
  );
}

function ComposeForm({
  templates,
  scope,
  onCreated,
}: {
  templates: WhatsappTemplate[];
  scope: WaScope;
  onCreated: (notice: ReactNode, kind?: 'success' | 'info' | 'error') => void;
}) {
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState(templates[0]?.id ?? '');
  const [variables, setVariables] = useState<string[]>([]);
  const [mediaUrl, setMediaUrl] = useState('');
  const [statuses, setStatuses] = useState<ContactStatus[]>(['SUBSCRIBED']);
  const [countryCodes, setCountryCodes] = useState('');
  const [optedInSince, setOptedInSince] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [sendNow, setSendNow] = useState(false);

  const template = templates.find((t) => t.id === templateId) ?? templates[0] ?? null;
  const header = template?.components.find((c) => c.type === 'HEADER');
  const needsMedia = header
    ? ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(String(header.format ?? ''))
    : false;
  const variableCount = template?.variableCount ?? 0;

  const create = useWaCreateCampaign(scope);

  const onSubmit = () => {
    if (!template) return;
    create.mutate(
      {
        body: {
          name: name.trim(),
          templateId: template.id,
          variables: variables.slice(0, variableCount),
          ...(mediaUrl.trim() ? { mediaUrl: mediaUrl.trim() } : {}),
          segment: {
            statuses,
            ...(countryCodes.trim()
              ? {
                  countryCodes: countryCodes
                    .split(',')
                    .map((c) => c.trim().replace(/\D/g, ''))
                    .filter(Boolean),
                }
              : {}),
            ...(optedInSince ? { optedInSince: new Date(optedInSince).toISOString() } : {}),
          },
          ...(scheduledAt ? { scheduledAt: new Date(scheduledAt).toISOString() } : {}),
        },
        send:
          sendNow || scheduledAt
            ? scheduledAt
              ? { scheduledAt: new Date(scheduledAt).toISOString() }
              : {}
            : null,
      },
      {
        onSuccess: (final) => {
          setName('');
          setVariables([]);
          setMediaUrl('');
          setScheduledAt('');
          onCreated(
            <>
              {final.status === 'DRAFT'
                ? 'Drafted'
                : final.status === 'SCHEDULED'
                  ? 'Scheduled'
                  : 'Started'}{' '}
              <strong>{final.name}</strong>
              {final.status !== 'DRAFT'
                ? ` for ${final.recipientsTotal} contact${final.recipientsTotal === 1 ? '' : 's'}`
                : ''}
              .
            </>,
          );
        },
      },
    );
  };

  return (
    <form
      className="form"
      aria-label="New campaign"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      noValidate
    >
      {create.error ? <Alert kind="error">{errorMessage(create.error)}</Alert> : null}
      <div className="solution">
        <div className="stack">
          <div className="form form--grid">
            <TextField
              label="Campaign name"
              name="campaignName"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <SelectField
              label="Template"
              value={template?.id ?? ''}
              onChange={(e) => {
                setTemplateId(e.target.value);
                setVariables([]);
              }}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.category.toLowerCase()}, {t.language})
                </option>
              ))}
            </SelectField>
            {Array.from({ length: variableCount }, (_, i) => (
              <TextField
                key={i}
                label={`Value for {{${i + 1}}}`}
                name={`variable${i + 1}`}
                value={variables[i] ?? ''}
                hint={i === 0 ? 'Type {{name}} to use each contact’s name.' : undefined}
                onChange={(e) => {
                  const next = [...variables];
                  next[i] = e.target.value;
                  setVariables(next);
                }}
              />
            ))}
            {needsMedia ? (
              <TextField
                label="Media URL (https)"
                name="mediaUrl"
                required
                value={mediaUrl}
                hint="A public image, video or document for the template’s header."
                onChange={(e) => setMediaUrl(e.target.value)}
              />
            ) : null}
            <TextField
              label="Country codes (optional)"
              name="countryCodes"
              placeholder="1, 44"
              value={countryCodes}
              onChange={(e) => setCountryCodes(e.target.value)}
            />
            <TextField
              label="Opted in since (optional)"
              name="optedInSince"
              type="date"
              value={optedInSince}
              onChange={(e) => setOptedInSince(e.target.value)}
            />
            <TextField
              label="Schedule for (optional)"
              name="scheduledAt"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
          </div>
          <div className="form__row">
            {(['SUBSCRIBED', 'PENDING'] as ContactStatus[]).map((s) => (
              <label key={s} className="field field--check">
                <input
                  type="checkbox"
                  checked={statuses.includes(s)}
                  onChange={() =>
                    setStatuses((cur) =>
                      cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s],
                    )
                  }
                />{' '}
                {s === 'SUBSCRIBED'
                  ? 'Subscribed contacts'
                  : 'Include contacts without consent (they will be skipped)'}
              </label>
            ))}
          </div>
          <label className="field field--check">
            <input
              type="checkbox"
              checked={sendNow}
              onChange={(e) => setSendNow(e.target.checked)}
            />{' '}
            Send as soon as it is created (otherwise it stays a draft you can preview first)
          </label>
          <div>
            <Button
              type="submit"
              loading={create.isPending}
              disabled={!name.trim() || !template || (needsMedia && !mediaUrl.trim())}
            >
              {scheduledAt ? 'Schedule campaign' : sendNow ? 'Create and send' : 'Save draft'}
            </Button>
          </div>
        </div>
        <div className="stack">
          <p className="muted">Preview</p>
          {template ? (
            <WhatsAppPreview
              template={template}
              variables={variables.map((v) => v.replace(/\{\{\s*name\s*\}\}/gi, 'Ada'))}
            />
          ) : null}
        </div>
      </div>
    </form>
  );
}
